import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Box, Typography, useTheme, Card, CardContent, Grid, Chip } from '@mui/material';
import { IFilterState } from '../../contexts/FilterContext';
import TableWidget, { HeaderMappingItem } from '../../helpers/tableBuilder';
import fetchData from '../../helpers/fetchData';
import { tokens } from '../../theme';
import Header from '../../components/Header';

interface ClientStatsData {
  client: string;
  client_name: string;
  date: string;
  total_subscribed: number;
  active_subscribed: number;
  inactive_subscribed: number;
  werbe_sperre_count: number;
  neue_coreg_count: number;
  active_werbe_sperre: number;
  active_neue_coreg: number;
  inactive_werbe_sperre: number;
  inactive_neue_coreg: number;
  sendable_ads: number;
  sendable_newsletter: number;
  sendable_all: number;
}

const ClientStats = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [latestStats, setLatestStats] = useState<ClientStatsData[]>([]);

  // Fetch latest stats for summary cards
  useEffect(() => {
    const fetchLatestStats = async () => {
      try {
        const response = (await fetchData('/api/mc-client-stats/latest', {}, false)) as any;
        if (response.success) {
          setLatestStats(response.data);
        }
      } catch (error) {
        console.error('Error fetching latest client stats:', error);
      }
    };

    fetchLatestStats();
  }, []);

  // getData function for TableWidget
  const getData = async (filterState: IFilterState): Promise<ClientStatsData[]> => {
    if (!filterState.startDate || !filterState.endDate) return [];

    const startDate = moment(filterState.startDate).format('YYYY-MM-DD');
    const endDate = moment(filterState.endDate).format('YYYY-MM-DD');

    const response = (await fetchData(
      `/api/mc-client-stats?startDate=${startDate}&endDate=${endDate}`,
      {},
    )) as any;

    if (response.success) {
      return response.data;
    }
    return [];
  };

  const headerMapping: Record<string, HeaderMappingItem> = {
    date: { name: 'Datum', type: 'date' },
    client_name: { name: 'Kunde', type: 'string' },
    total_subscribed: { name: 'Gesamt', type: 'number' },
    active_subscribed: { name: 'Aktiv', type: 'number' },
    inactive_subscribed: { name: 'Inaktiv', type: 'number' },
    sendable_ads: { name: 'Sendbar Ads', type: 'number' },
    sendable_newsletter: { name: 'Sendbar NL', type: 'number' },
    sendable_all: { name: 'Sendbar Alle', type: 'number' },
    werbe_sperre_count: { name: 'Werbe-Sperre', type: 'number' },
    neue_coreg_count: { name: 'Neue Coreg', type: 'number' },
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString('de-DE');
  };

  // Summary stats per client from latest data
  const SummaryCards = () => {
    if (latestStats.length === 0) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          <Typography color="textSecondary">
            Keine aktuellen Daten vorhanden. Scheduler noch nicht gelaufen?
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {latestStats.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.client}>
            <Card
              sx={{
                backgroundColor: '#ffffff',
                border: `1px solid ${colors.grey[300]}`,
              }}
            >
              <CardContent>
                <Typography variant="h6" color="textPrimary" gutterBottom>
                  {stat.client_name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Stand: {stat.date}
                </Typography>

                <Box mt={2}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Gesamt
                      </Typography>
                      <Typography variant="h6" color="textPrimary">
                        {formatNumber(stat.total_subscribed)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Aktiv
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {formatNumber(stat.active_subscribed)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Sendbar Ads
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        {formatNumber(stat.sendable_ads)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Sendbar NL
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        {formatNumber(stat.sendable_newsletter)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                  <Chip
                    label={`Werbe-Sperre: ${formatNumber(stat.werbe_sperre_count)}`}
                    size="small"
                    sx={{ backgroundColor: colors.redAccent?.[700] || '#d32f2f', color: 'white' }}
                  />
                  <Chip
                    label={`Neue Coreg: ${formatNumber(stat.neue_coreg_count)}`}
                    size="small"
                    sx={{ backgroundColor: colors.blueAccent?.[700] || '#1976d2', color: 'white' }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Compute summary for table
  const computeSummary = (data: ClientStatsData[]): ClientStatsData => {
    if (!data.length) {
      return {
        client: 'total',
        client_name: 'Insgesamt',
        date: '',
        total_subscribed: 0,
        active_subscribed: 0,
        inactive_subscribed: 0,
        werbe_sperre_count: 0,
        neue_coreg_count: 0,
        active_werbe_sperre: 0,
        active_neue_coreg: 0,
        inactive_werbe_sperre: 0,
        inactive_neue_coreg: 0,
        sendable_ads: 0,
        sendable_newsletter: 0,
        sendable_all: 0,
      };
    }

    // Group by date and sum across clients
    const latestDate = data.reduce((max, row) => (row.date > max ? row.date : max), data[0].date);

    const latestData = data.filter((row) => row.date === latestDate);

    const summary: ClientStatsData = {
      client: 'total',
      client_name: 'Insgesamt',
      date: latestDate,
      total_subscribed: 0,
      active_subscribed: 0,
      inactive_subscribed: 0,
      werbe_sperre_count: 0,
      neue_coreg_count: 0,
      active_werbe_sperre: 0,
      active_neue_coreg: 0,
      inactive_werbe_sperre: 0,
      inactive_neue_coreg: 0,
      sendable_ads: 0,
      sendable_newsletter: 0,
      sendable_all: 0,
    };

    latestData.forEach((row) => {
      summary.total_subscribed += row.total_subscribed || 0;
      summary.active_subscribed += row.active_subscribed || 0;
      summary.inactive_subscribed += row.inactive_subscribed || 0;
      summary.werbe_sperre_count += row.werbe_sperre_count || 0;
      summary.neue_coreg_count += row.neue_coreg_count || 0;
      summary.active_werbe_sperre += row.active_werbe_sperre || 0;
      summary.active_neue_coreg += row.active_neue_coreg || 0;
      summary.inactive_werbe_sperre += row.inactive_werbe_sperre || 0;
      summary.inactive_neue_coreg += row.inactive_neue_coreg || 0;
      summary.sendable_ads += row.sendable_ads || 0;
      summary.sendable_newsletter += row.sendable_newsletter || 0;
      summary.sendable_all += row.sendable_all || 0;
    });

    return summary;
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="Externe Kunden Statistiken"
          subtitle="Mailchimp Subscriber & Tag-basierte Segmentierung"
        />
      </Box>

      <Box mb={3}>
        <Typography variant="body2" color="textSecondary">
          <strong>Werbe-Sperre:</strong> 60 Tage Block von Werbemailings (Webinar, PLX) |{' '}
          <strong>Neue Coreg:</strong> 30 Tage Block von Newsletter | <strong>Inaktiv:</strong>{' '}
          Keine Opens seit 3+ Monaten
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Typography variant="h5" fontWeight="600" mb={2}>
        Aktuelle Werte
      </Typography>
      <SummaryCards />

      {/* Historical Table */}
      <Typography variant="h5" fontWeight="600" mb={2}>
        Historische Daten
      </Typography>

      <TableWidget<ClientStatsData>
        getData={getData}
        detailsHeaderMapping={headerMapping}
        computeSummary={computeSummary}
        initialSortColumn="date"
        sortOrder="desc"
        excelDownloadName="mc_client_stats"
      />
    </Box>
  );
};

export default ClientStats;
